import { PropertyRealStateDetailInfo, AddPropertyPayload } from "./propertiesTypes";
import { propertyService } from "./propertyServices";
import { tokenService } from "@/services/auth/token.service";

export interface GuestPropertyItem {
  property_id: string; // Permanent property ID
  address: string;
  streetAddress: string;
  cityStateZip: string;
  city?: string;
  state?: string;
  zip?: string;
  latitude?: number;
  longitude?: number;
  assessedValue: number;
  marketValue: number;
  potentialSavings: number;
  potentialSavingsPercent?: number;
  propertyType?: string;
  yearBuilt?: number | string;
  parcelId?: string;
  lotSize?: string;
  taxYear?: number | string;
  squareFeet?: number | string;
  schoolDistrict?: string;
  ownerDetails?: {
    ownerName?: string;
    email?: string;
    mailingAddress?: string;
    phone?: string;
  };
  selected: boolean;
  protestFiled?: boolean;
  paymentStatus?: string | null;
  payment_status?: "paid" | "unpaid" | null;
  rawDetails?: PropertyRealStateDetailInfo;
}

const GUEST_SESSION_KEY = "guest_session_id";
const GUEST_PROPERTIES_PREFIX = "guest_properties_";
const DEVICE_ID_KEY = "device_id";

/**
 * Get or create unique persistent device_id stored in localStorage.
 */
export const getDeviceId = (): string => {
  let deviceId = localStorage.getItem(DEVICE_ID_KEY);

  if (!deviceId) {
    const randomPrefix = Math.random().toString(36).substring(2, 10);
    const ua = typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 15) : "Mozilla/5.0";
    deviceId = `${randomPrefix}-${ua}`;
    localStorage.setItem(DEVICE_ID_KEY, deviceId);
  }

  return deviceId;
};

/**
 * Requirement 1: Guest Session ID
 * Get or create unique guest session ID. Reuses existing guest_session_id from localStorage.
 */
export const getGuestSessionId = (): string => {
  let guestSessionId = localStorage.getItem(GUEST_SESSION_KEY);

  if (!guestSessionId) {
    guestSessionId = crypto.randomUUID();
    localStorage.setItem(GUEST_SESSION_KEY, guestSessionId);
  }

  return guestSessionId;
};

const getStorageKeyForProperty = (guestSessionId: string, propertyId: string): string => {
  return `${GUEST_PROPERTIES_PREFIX}${guestSessionId}_${propertyId}`;
};

const getStorageKeyIndex = (guestSessionId: string): string => {
  return `${GUEST_PROPERTIES_PREFIX}${guestSessionId}_index`;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

/** Backend payment/protest flows expect a UUID-shaped property id (with or without hyphens). */
export const looksLikePropertyUuid = (value: string): boolean => {
  const v = value.trim();
  if (!v) return false;
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v)) {
    return true;
  }
  return /^[0-9a-f]{32}$/i.test(v);
};

export const isValidUUID = (id?: string | null): boolean => {
  if (!id) return false;
  return looksLikePropertyUuid(id);
};

const pickPropertyIdFromRecord = (rec: Record<string, unknown>): string | null => {
  for (const key of ["property_id", "propertyId"] as const) {
    const v = rec[key];
    if (typeof v === "string" && looksLikePropertyUuid(v)) return v.trim();
  }
  const idVal = rec["id"];
  if (typeof idVal === "string" && looksLikePropertyUuid(idVal)) return idVal.trim();
  return null;
};

/**
 * Extract canonical `property_id` from `propertyService.add` envelope.
 * Supports `data` as object, nested `data.data`, or string id.
 */
export const extractCreatedPropertyIdFromAddResponse = (res: unknown): string | null => {
  if (!isRecord(res)) return null;
  const top = pickPropertyIdFromRecord(res);
  if (top) return top;

  const data = res["data"];
  if (typeof data === "string" && looksLikePropertyUuid(data)) return data.trim();
  if (isRecord(data)) {
    const fromData = pickPropertyIdFromRecord(data);
    if (fromData) return fromData;
    const inner = data["data"];
    if (isRecord(inner)) {
      const fromInner = pickPropertyIdFromRecord(inner);
      if (fromInner) return fromInner;
    }
  }
  return null;
};

// Initial default properties matching US design specifications
const DEFAULT_GUEST_PROPERTIES: GuestPropertyItem[] = [
  {
    property_id: "prop_211_blanton_gainesville_tx",
    address: "211 1/2 Blanton St, Gainesville, TX",
    streetAddress: "211 1/2 Blanton St",
    cityStateZip: "Gainesville, TX, 76240",
    city: "Gainesville",
    state: "TX",
    zip: "76240",
    assessedValue: 58663,
    marketValue: 75000,
    potentialSavings: 246,
    potentialSavingsPercent: 4.2,
    propertyType: "Residential",
    yearBuilt: 2004,
    parcelId: "apn_76240_001",
    lotSize: "0.35 Acres",
    taxYear: 2025,
    squareFeet: "1,800 sqft",
    schoolDistrict: "Gainesville ISD",
    ownerDetails: {
      ownerName: "Property Owner",
      email: "owner@example.com",
      mailingAddress: "211 1/2 Blanton St, Gainesville, TX, 76240",
      phone: "(555) 123-4567",
    },
    selected: false,
    protestFiled: false,
  },
  {
    property_id: "prop_4311_ludwig_stafford_tx_1",
    address: "4311 1/2 Ludwig Ln, Stafford, TX",
    streetAddress: "4311 1/2 Ludwig Ln",
    cityStateZip: "Stafford, TX, 77477",
    city: "Stafford",
    state: "TX",
    zip: "77477",
    assessedValue: 30810,
    marketValue: 45000,
    potentialSavings: 129,
    potentialSavingsPercent: 4.2,
    propertyType: "Residential",
    yearBuilt: 2008,
    parcelId: "apn_77477_002",
    lotSize: "0.25 Acres",
    taxYear: 2025,
    squareFeet: "1,450 sqft",
    schoolDistrict: "Fort Bend ISD",
    ownerDetails: {
      ownerName: "Property Owner",
      email: "owner@example.com",
      mailingAddress: "4311 1/2 Ludwig Ln, Stafford, TX, 77477",
      phone: "(555) 234-5678",
    },
    selected: false,
    protestFiled: false,
  },
  {
    property_id: "prop_4311_ludwig_stafford_tx_2",
    address: "4311 1/2 Ludwig Ln, Stafford, TX",
    streetAddress: "4311 1/2 Ludwig Ln",
    cityStateZip: "Stafford, TX, 77477",
    city: "Stafford",
    state: "TX",
    zip: "77477",
    assessedValue: 337733,
    marketValue: 420000,
    potentialSavings: 1418,
    potentialSavingsPercent: 4.2,
    propertyType: "Residential",
    yearBuilt: 2015,
    parcelId: "apn_77477_003",
    lotSize: "0.55 Acres",
    taxYear: 2025,
    squareFeet: "3,100 sqft",
    schoolDistrict: "Fort Bend ISD",
    ownerDetails: {
      ownerName: "Property Owner",
      email: "owner@example.com",
      mailingAddress: "4311 1/2 Ludwig Ln, Stafford, TX, 77477",
      phone: "(555) 345-6789",
    },
    selected: false,
    protestFiled: false,
  },
  {
    property_id: "prop_4311_ludwig_stafford_tx_3",
    address: "4311 1/2 Ludwig Ln, Stafford, TX",
    streetAddress: "4311 1/2 Ludwig Ln",
    cityStateZip: "Stafford, TX, 77477",
    city: "Stafford",
    state: "TX",
    zip: "77477",
    assessedValue: 337733,
    marketValue: 420000,
    potentialSavings: 1418,
    potentialSavingsPercent: 4.2,
    propertyType: "Residential",
    yearBuilt: 2015,
    parcelId: "apn_77477_004",
    lotSize: "0.55 Acres",
    taxYear: 2025,
    squareFeet: "3,100 sqft",
    schoolDistrict: "Fort Bend ISD",
    ownerDetails: {
      ownerName: "Property Owner",
      email: "owner@example.com",
      mailingAddress: "4311 1/2 Ludwig Ln, Stafford, TX, 77477",
      phone: "(555) 456-7890",
    },
    selected: false,
    protestFiled: false,
  },
  {
    property_id: "prop_4311_ludwig_marietta_oh_1",
    address: "4311 1/2 Ludwig Ln, Stafford, TX",
    streetAddress: "4311 1/2 Ludwig Ln",
    cityStateZip: "Marietta, OH, 45750",
    city: "Marietta",
    state: "OH",
    zip: "45750",
    assessedValue: 76300,
    marketValue: 98000,
    potentialSavings: 320,
    potentialSavingsPercent: 4.2,
    propertyType: "Residential",
    yearBuilt: 1998,
    parcelId: "apn_45750_005",
    lotSize: "0.40 Acres",
    taxYear: 2025,
    squareFeet: "1,650 sqft",
    schoolDistrict: "Marietta City School District",
    ownerDetails: {
      ownerName: "Property Owner",
      email: "owner@example.com",
      mailingAddress: "4311 1/2 Ludwig Ln, Marietta, OH, 45750",
      phone: "(555) 567-8901",
    },
    selected: false,
    protestFiled: false,
  },
  {
    property_id: "prop_4311_ludwig_marietta_oh_2",
    address: "4311 1/2 Ludwig Ln, Stafford, TX",
    streetAddress: "4311 1/2 Ludwig Ln",
    cityStateZip: "Marietta, OH, 45750",
    city: "Marietta",
    state: "OH",
    zip: "45750",
    assessedValue: 76300,
    marketValue: 98000,
    potentialSavings: 320,
    potentialSavingsPercent: 4.2,
    propertyType: "Residential",
    yearBuilt: 1998,
    parcelId: "apn_45750_006",
    lotSize: "0.40 Acres",
    taxYear: 2025,
    squareFeet: "1,650 sqft",
    schoolDistrict: "Marietta City School District",
    ownerDetails: {
      ownerName: "Property Owner",
      email: "owner@example.com",
      mailingAddress: "4311 1/2 Ludwig Ln, Marietta, OH, 45750",
      phone: "(555) 678-9012",
    },
    selected: false,
    protestFiled: false,
  },
  {
    property_id: "prop_6456_south_haven_mi",
    address: "6456 104th Ave, South Haven, MI",
    streetAddress: "6456 104th Ave",
    cityStateZip: "South Haven, MI, 49900",
    city: "South Haven",
    state: "MI",
    zip: "49900",
    assessedValue: 266100,
    marketValue: 315000,
    potentialSavings: 1118,
    potentialSavingsPercent: 4.2,
    propertyType: "Residential",
    yearBuilt: 2005,
    parcelId: "apn_49900_007",
    lotSize: "0.75 Acres",
    taxYear: 2025,
    squareFeet: "2,450 sqft",
    schoolDistrict: "South Haven Public Schools",
    ownerDetails: {
      ownerName: "Property Owner",
      email: "owner@example.com",
      mailingAddress: "6456 104th Ave, South Haven, MI, 49900",
      phone: "(555) 789-0123",
    },
    selected: false,
    protestFiled: false,
  },
  {
    property_id: "prop_345_puyallup_wa",
    address: "345 1/2 3rd St SE, Puyallup, WA",
    streetAddress: "345 1/2 3rd St SE",
    cityStateZip: "Puyallup, WA, 98372",
    city: "Puyallup",
    state: "WA",
    zip: "98372",
    assessedValue: 289600,
    marketValue: 340000,
    potentialSavings: 1216,
    potentialSavingsPercent: 4.2,
    propertyType: "Residential",
    yearBuilt: 2002,
    parcelId: "apn_98372_008",
    lotSize: "0.32 Acres",
    taxYear: 2025,
    squareFeet: "2,200 sqft",
    schoolDistrict: "Puyallup School District",
    ownerDetails: {
      ownerName: "Property Owner",
      email: "owner@example.com",
      mailingAddress: "345 1/2 3rd St SE, Puyallup, WA, 98372",
      phone: "(555) 890-1234",
    },
    selected: false,
    protestFiled: false,
  },
];

/**
 * Requirement 3 & 4: Get all properties associated with the current guest session.
 * Stores relationship using: guest_session_id + property_id
 */
export const getGuestProperties = (): GuestPropertyItem[] => {
  const guestSessionId = getGuestSessionId();
  const indexKey = getStorageKeyIndex(guestSessionId);
  const rawIndex = localStorage.getItem(indexKey);

  if (!rawIndex) {
    // Seed default properties if first visit
    saveGuestProperties(DEFAULT_GUEST_PROPERTIES);
    return DEFAULT_GUEST_PROPERTIES;
  }

  try {
    const propertyIds: string[] = JSON.parse(rawIndex);
    const properties: GuestPropertyItem[] = [];

    for (const propId of propertyIds) {
      const propKey = getStorageKeyForProperty(guestSessionId, propId);
      const rawProp = localStorage.getItem(propKey);
      if (rawProp) {
        properties.push(JSON.parse(rawProp));
      }
    }

    if (properties.length === 0) {
      saveGuestProperties(DEFAULT_GUEST_PROPERTIES);
      return DEFAULT_GUEST_PROPERTIES;
    }

    return properties;
  } catch (err) {
    console.error("Error reading guest properties from storage", err);
    saveGuestProperties(DEFAULT_GUEST_PROPERTIES);
    return DEFAULT_GUEST_PROPERTIES;
  }
};

/**
 * Internal helper to update full list in localStorage
 */
export const saveGuestProperties = (properties: GuestPropertyItem[]): void => {
  const guestSessionId = getGuestSessionId();
  const indexKey = getStorageKeyIndex(guestSessionId);

  const propertyIds = properties.map((p) => p.property_id);
  localStorage.setItem(indexKey, JSON.stringify(propertyIds));

  for (const prop of properties) {
    const propKey = getStorageKeyForProperty(guestSessionId, prop.property_id);
    localStorage.setItem(propKey, JSON.stringify(prop));
  }
};

/**
 * Requirement 2: Add Property — No Login
 * Associate property with current guest_session_id using guest_session_id + property_id.
 * Also calls backend API passing guest session ID and property ID.
 */
export const addGuestProperty = async (
  propertyData: Omit<GuestPropertyItem, "selected"> & { selected?: boolean },
  userId?: string
): Promise<GuestPropertyItem> => {
  const guestSessionId = getGuestSessionId();

  // Ensure property_id is a valid UUID, preserving any APN as parcelId
  const initialPropId = isValidUUID(propertyData.property_id)
    ? propertyData.property_id
    : crypto.randomUUID();

  const newProperty: GuestPropertyItem = {
    ...propertyData,
    property_id: initialPropId,
    parcelId:
      propertyData.parcelId ||
      (!isValidUUID(propertyData.property_id) ? propertyData.property_id : undefined),
    selected: propertyData.selected ?? true,
  };

  // Call backend API: POST /api/property-service/properties
  try {
    const user = tokenService.getUser();
    const effectiveUserId = userId || (user?.id ? String(user.id) : undefined);
    const rawDetails = newProperty.rawDetails;

    const rawDict = (rawDetails || {}) as unknown as Record<string, unknown>;

    const propId = newProperty.property_id;

    const street =
      rawDetails?.propertyInfo?.address?.street ||
      newProperty.streetAddress ||
      newProperty.address.split(",")[0]?.trim() ||
      "";
    const city = rawDetails?.propertyInfo?.address?.city || newProperty.city || "Medora";
    const state = rawDetails?.propertyInfo?.address?.state || newProperty.state || "ND";
    const zip = rawDetails?.propertyInfo?.address?.zip || newProperty.zip || "58645";
    const county = rawDetails?.propertyInfo?.address?.county || "Billings County";

    const owner1 =
      [rawDict.owner1FirstName, rawDict.owner1LastName].filter(Boolean).join(" ") ||
      newProperty.ownerDetails?.ownerName ||
      "Property Owner";
    const owner2 =
      [rawDict.owner2FirstName, rawDict.owner2LastName].filter(Boolean).join(" ") || "";

    const assessedVal = rawDetails?.taxInfo?.assessedValue || newProperty.assessedValue || 13500;
    const marketVal = rawDetails?.taxInfo?.marketValue || newProperty.marketValue || 27000;
    const improvementVal = rawDetails?.taxInfo?.assessedImprovementValue || 0;
    const marketLandVal = rawDetails?.taxInfo?.marketLandValue || marketVal;
    const taxAmt = rawDetails?.taxInfo?.taxAmount
      ? Number(rawDetails.taxInfo.taxAmount)
      : Math.round(assessedVal * 0.01) || 147.99;

    const payload: AddPropertyPayload = {
      ...(effectiveUserId ? { user_id: String(effectiveUserId) } : {}),
      ipAddress: "",
      ip_address: "",
      deviceId: getDeviceId(),
      device_id: getDeviceId(),
      street,
      city,
      state,
      zip,
      unitNumber: "",
      address: newProperty.address,
      propertyId: propId,
      createdAt: new Date().toISOString(),
      isMailingAddress: true,
      taxInfo: {
        improvementValue: Number(improvementVal),
        marketLandValue: Number(marketLandVal),
        marketValue: Number(marketVal),
        taxAmount: Number(taxAmt),
        assessedValue: Number(assessedVal),
        taxDelinquentYear: null,
        taxYear: 2025,
      },
      ownerName: owner1,
      propertyUserType: "owner",
      apiPropertyId: propId,
      latitude: rawDetails?.propertyInfo?.latitude
        ? Number(rawDetails.propertyInfo.latitude)
        : 46.91435909744423,
      longitude: rawDetails?.propertyInfo?.longitude
        ? Number(rawDetails.propertyInfo.longitude)
        : -103.52396217258867,
      county,
      estimatedValue: Number(assessedVal),
      propertyType: rawDetails?.propertyType || newProperty.propertyType || "OTHER",
      yearBuilt: newProperty.yearBuilt ? Number(newProperty.yearBuilt) : 0,
      squareFeet: 0,
      bedrooms: 0,
      bathrooms: 0,
      lotSquareFeet: 0,
      ownerInfo: {
        owner1FullName: owner1,
        owner2FullName: owner2,
        ownerOccupied: Boolean(rawDict.ownerOccupied),
        ownershipLength: Number(rawDict.yearsOwned || 0),
      },
      estimatedMortgageBalance: 0,
      estimatedEquity: Number(assessedVal),
      equityPercent: 100,
      lastSaleDate: null,
      lastSalePrice: 0,
      lastApiSync: new Date().toISOString(),
      apiAddress: {
        fullAddress: newProperty.address,
        street,
        city,
        state,
        zip,
        county,
      },
      propertyRawType: rawDetails?.propertyType || newProperty.propertyType || "OTHER",
    };

    const res = await propertyService.add(payload);
    const backendPropertyId = extractCreatedPropertyIdFromAddResponse(res);
    if (backendPropertyId && isValidUUID(backendPropertyId)) {
      newProperty.property_id = backendPropertyId;
    }
  } catch (apiErr) {
    console.warn(
      "Call to POST /api/property-service/properties failed or guest mode fallback:",
      apiErr
    );
  }

  // Store relationship: guest_session_id + property_id
  const propKey = getStorageKeyForProperty(guestSessionId, newProperty.property_id);
  localStorage.setItem(propKey, JSON.stringify(newProperty));

  // Update index
  const existingProperties = getGuestProperties();
  const exists = existingProperties.some((p) => p.property_id === newProperty.property_id);

  if (!exists) {
    const updatedProperties = [newProperty, ...existingProperties];
    saveGuestProperties(updatedProperties);
  } else {
    const updatedProperties = existingProperties.map((p) =>
      p.property_id === newProperty.property_id ? newProperty : p
    );
    saveGuestProperties(updatedProperties);
  }

  return newProperty;
};

/**
 * Requirement 3: Remove Property
 */
export const removeGuestProperty = async (propertyId: string): Promise<void> => {
  const guestSessionId = getGuestSessionId();
  const propKey = getStorageKeyForProperty(guestSessionId, propertyId);
  localStorage.removeItem(propKey);

  const existingProperties = getGuestProperties();
  const updatedProperties = existingProperties.filter((p) => p.property_id !== propertyId);

  const indexKey = getStorageKeyIndex(guestSessionId);
  const propertyIds = updatedProperties.map((p) => p.property_id);
  localStorage.setItem(indexKey, JSON.stringify(propertyIds));
};

/**
 * Toggle property selection
 */
export const toggleGuestPropertySelection = (propertyId: string): GuestPropertyItem[] => {
  const properties = getGuestProperties();
  const updated = properties.map((p) =>
    p.property_id === propertyId ? { ...p, selected: !p.selected } : p
  );
  saveGuestProperties(updated);
  return updated;
};

/**
 * Bulk select or clear selection
 */
export const setGuestPropertiesSelection = (selected: boolean): GuestPropertyItem[] => {
  const properties = getGuestProperties();
  const updated = properties.map((p) => ({ ...p, selected }));
  saveGuestProperties(updated);
  return updated;
};

/**
 * Get guest property by ID
 */
export const getGuestPropertyById = (propertyId: string): GuestPropertyItem | undefined => {
  const properties = getGuestProperties();
  return properties.find((p) => p.property_id === propertyId);
};

/**
 * Maps a raw property object from GET /api/property-service/properties/list to a GuestPropertyItem
 */
export const mapApiPropertyToGuestItem = (item: Record<string, unknown>): GuestPropertyItem => {
  const addrObj = (item.address || {}) as Record<string, unknown>;
  const ownerObj = (item.owner || {}) as Record<string, unknown>;
  const taxObj = (item.tax_info || {}) as Record<string, unknown>;

  const fullAddress = String(addrObj.full_address || addrObj.line1 || item.address || "");
  const streetAddress = String(
    addrObj.street || addrObj.line1 || fullAddress.split(",")[0]?.trim() || fullAddress
  );
  const city = String(addrObj.city || "");
  const state = String(addrObj.state || "");
  const zip = String(addrObj.zip || "");
  const cityStateZip = [city, state, zip].filter(Boolean).join(", ");

  const assessedValue = Number(
    taxObj.assessed_value ?? item.assessed_value ?? item.estimated_value ?? 13500
  );
  const marketValue = Number(taxObj.market_value ?? item.estimated_value ?? assessedValue ?? 27000);
  const potentialSavings = Math.round(assessedValue * 0.0042) || 323;

  const protestStatus = String(item.protest_status || "").toLowerCase();
  const protestFiled =
    protestStatus === "filed" || protestStatus === "in_progress" || item.payment_status === "paid";

  return {
    property_id: String(
      item.property_id || item.id || item.api_property_id || item.external_property_ref || ""
    ),
    address: fullAddress,
    streetAddress,
    cityStateZip,
    city,
    state,
    zip,
    latitude:
      typeof addrObj.latitude === "number"
        ? addrObj.latitude
        : addrObj.latitude
          ? Number(addrObj.latitude)
          : typeof item.latitude === "number"
            ? item.latitude
            : item.latitude
              ? Number(item.latitude)
              : undefined,
    longitude:
      typeof addrObj.longitude === "number"
        ? addrObj.longitude
        : addrObj.longitude
          ? Number(addrObj.longitude)
          : typeof item.longitude === "number"
            ? item.longitude
            : item.longitude
              ? Number(item.longitude)
              : undefined,
    assessedValue,
    marketValue,
    potentialSavings,
    potentialSavingsPercent: 4.23,
    propertyType: String(item.property_type || item.property_raw_type || "Residential"),
    yearBuilt: Number(item.year_built || 1998),
    parcelId: String(item.api_property_id || item.external_property_ref || item.property_id || ""),
    lotSize: item.lot_square_feet ? `${item.lot_square_feet} sqft` : "0.45 Acres",
    taxYear: Number(taxObj.tax_year || 2025),
    squareFeet: item.square_feet ? `${item.square_feet} sqft` : "2,150 sqft",
    schoolDistrict: "Local School District",
    ownerDetails: {
      ownerName: String(ownerObj.full_name || "Property Owner"),
      email: "owner@example.com",
      mailingAddress: fullAddress,
      phone: String(ownerObj.phone || "(555) 123-4567"),
    },
    selected: false,
    protestFiled,
    paymentStatus: item.payment_status ? String(item.payment_status).toLowerCase() : null,
    payment_status: (item.payment_status ? String(item.payment_status).toLowerCase() : null) as
      | "paid"
      | "unpaid"
      | null,
    rawDetails: item as unknown as PropertyRealStateDetailInfo,
  };
};

/**
 * Fetches live properties list from GET /api/property-service/properties/list
 */
export const fetchGuestPropertiesList = async (userId?: string): Promise<GuestPropertyItem[]> => {
  const localProps = getGuestProperties();
  try {
    const deviceId = getDeviceId();
    const user = tokenService.getUser();
    const effectiveUserId = userId || (user?.id ? String(user.id) : undefined);

    let queryParam = `device_id=${encodeURIComponent(deviceId)}`;
    if (effectiveUserId) {
      queryParam = `account_id=${encodeURIComponent(effectiveUserId)}`;
    }

    const apiProps = await propertyService.getAll(queryParam);
    if (Array.isArray(apiProps)) {
      const existingSelectionMap = new Map(localProps.map((p) => [p.property_id, p.selected]));
      const mapped = apiProps.map((item) => {
        const guestItem = mapApiPropertyToGuestItem(item as unknown as Record<string, unknown>);
        if (existingSelectionMap.has(guestItem.property_id)) {
          guestItem.selected = Boolean(existingSelectionMap.get(guestItem.property_id));
        }
        return guestItem;
      });
      saveGuestProperties(mapped);
      return mapped;
    }
  } catch (err) {
    console.warn("Failed to fetch property list API, returning local guest properties:", err);
  }
  return localProps;
};
